import React, { useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import AppToasts from '@/components/AppToasts';
import { colors } from '@/constants/colors';
import useAuthStore from '@/store/authStore';
import { useUIStore } from '@/store/useUIStore';
import PromptWrapper from '@/components/Admin/Templates/PromptWrapper';
import { getPartnerBySlugApi, type ApiPublicPartner } from '@/api/vendo';
import { rememberSeller, signInPathForSeller } from '@/utils/sellerReturn';

/**
 * /s/<slug> — one seller's own page inside the app.
 *
 * Why it exists: the shareable landing page (smartfi.ph/p/<slug>) used to send
 * shoppers to the whole partners marketplace, which threw the seller away on
 * arrival — the buyer had to find them again in a grid of cards. Worse, the old
 * link pointed at the legacy /partners address, and that redirect drops the
 * query string, so even the ?seller= hint never survived the trip.
 *
 * Public on purpose. Someone tapping a Facebook link is a stranger; making them
 * sign in before they can even see who they are buying from loses the sale.
 * Buy Now is where the account is required, and the seller is remembered across
 * that detour (see utils/sellerReturn).
 *
 * Layout is deliberately a storefront rather than another dashboard screen:
 * profile on the left, a checkout panel on the right that totals as you change
 * the quantity, then the existing buy form opens with that quantity carried in.
 */

const initialsOf = (name: string) =>
   name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');

const SellerPage: React.FC = () => {
   const { slug = '' } = useParams<{ slug: string }>();
   const navigate = useNavigate();
   const { isAuthenticated } = useAuthStore();
   const { handleShow, show, template, title, data: uiData, actions } = useUIStore();

   const {
      data: seller,
      isLoading,
      isError,
   } = useQuery({
      queryKey: ['public-seller', slug],
      queryFn: () => getPartnerBySlugApi(slug),
      enabled: Boolean(slug),
      retry: false,
   });

   const [qty, setQty] = useState(1);

   const stock = seller?.availableCredits ?? 0;
   const price = seller?.basePricePerCredit ?? 500;
   // Clamp against stock so the total can never promise more than the seller
   // can actually hand over — the server rejects it anyway, but finding out
   // after filling in the form is a bad way to learn.
   const maxQty = Math.max(1, stock);
   const clampedQty = Math.min(Math.max(1, qty), maxQty);
   const total = useMemo(() => clampedQty * price, [clampedQty, price]);

   const openBuyForm = (s: ApiPublicPartner) => {
      if (s.kind === 'reseller') {
         handleShow({
            template: 'inquire-reseller',
            title: `Buy credits from ${s.displayName}`,
            windowType: 'desktop',
            data: {
               _id: s.id,
               displayName: s.displayName,
               slug: s.slug,
               photoUrl: s.photoUrl,
               blurb: s.blurb,
               contactPhone: s.contactPhone,
               facebookUrl: s.facebookUrl,
               availableCredits: s.availableCredits,
               initialCredits: clampedQty,
            },
         });
         return;
      }
      handleShow({
         template: 'inquire-credits',
         title: `Request credits from ${s.displayName}`,
         windowType: 'desktop',
         data: { id: s.id, credits: clampedQty },
      });
   };

   const handleBuy = () => {
      if (!seller) return;
      if (!isAuthenticated) {
         // Belt and braces: the note survives moving between the sign-in and
         // sign-up forms, the URL survives a reload of that screen.
         rememberSeller(slug);
         navigate(signInPathForSeller(slug));
         return;
      }
      openBuyForm(seller);
   };

   if (isLoading) {
      return (
         <Shell>
            <TopBar>
               <Brand>SmartFi</Brand>
            </TopBar>
            <Center>
               <SkeletonLines lines={3} />
            </Center>
         </Shell>
      );
   }

   if (isError || !seller) {
      return (
         <Shell>
            <TopBar>
               <Brand>SmartFi</Brand>
            </TopBar>
            <Center>
               <NotFoundCard>
                  <NotFoundTitle>This seller link is not active</NotFoundTitle>
                  <NotFoundText>
                     The link may be mistyped, or this partner is no longer
                     selling. You can still browse every approved seller.
                  </NotFoundText>
                  <GhostBtn onClick={() => navigate('/wifi-partners')}>
                     Browse all sellers
                  </GhostBtn>
               </NotFoundCard>
            </Center>
         </Shell>
      );
   }

   const roleLabel = seller.kind === 'distributor' ? 'Distributor' : 'Reseller';
   const location = [seller.city, seller.region].filter(Boolean).join(', ');
   const soldOut = !seller.canSell || stock <= 0;

   return (
      <Shell>
         <AppToasts />
         <PromptWrapper
            show={show}
            template={template ?? ''}
            title={title ?? ''}
            data={uiData}
            actions={actions}
         />

         <TopBar>
            <Brand>SmartFi</Brand>
            {isAuthenticated ? (
               <GhostBtn onClick={() => navigate('/home')}>My dashboard</GhostBtn>
            ) : (
               <GhostBtn onClick={() => navigate('/')}>Sign in</GhostBtn>
            )}
         </TopBar>

         <Columns>
            {/* ── Left: who they are ───────────────────────────────── */}
            <ProfileCol>
               <ProfileHead>
                  <AvatarWrap $kind={seller.kind}>
                     {seller.photoUrl && (
                        <Avatar
                           src={seller.photoUrl}
                           alt={seller.displayName}
                           onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                           }}
                        />
                     )}
                     <AvatarFallback $kind={seller.kind}>
                        {initialsOf(seller.displayName)}
                     </AvatarFallback>
                  </AvatarWrap>
                  <HeadText>
                     <RoleTag $kind={seller.kind}>
                        Authorized SmartFi {roleLabel}
                     </RoleTag>
                     <SellerName>{seller.displayName}</SellerName>
                     {location && <Location>{location}</Location>}
                  </HeadText>
               </ProfileHead>

               {seller.blurb && <Blurb>{seller.blurb}</Blurb>}

               <SectionTitle>What you get</SectionTitle>
               <Perks>
                  <Perk>
                     <PerkIcon>🔑</PerkIcon>
                     <div>
                        <PerkName>1 credit = 1 device, lifetime license</PerkName>
                        <PerkSub>Works on a main vendo or a sub-vendo.</PerkSub>
                     </div>
                  </Perk>
                  <Perk>
                     <PerkIcon>⚡</PerkIcon>
                     <div>
                        <PerkName>Activated after payment</PerkName>
                        <PerkSub>
                           Credits land in your account once {seller.displayName}{' '}
                           confirms.
                        </PerkSub>
                     </div>
                  </Perk>
                  <Perk>
                     <PerkIcon>🛡️</PerkIcon>
                     <div>
                        <PerkName>Genuine SmartFi licenses</PerkName>
                        <PerkSub>
                           Approved by SmartFi, with full support included.
                        </PerkSub>
                     </div>
                  </Perk>
               </Perks>

               {(seller.contactPhone || seller.facebookUrl) && (
                  <>
                     <SectionTitle>Contact</SectionTitle>
                     <Contacts>
                        {seller.contactPhone && (
                           <ContactLink href={`tel:${seller.contactPhone}`}>
                              📞 {seller.contactPhone}
                           </ContactLink>
                        )}
                        {seller.facebookUrl && (
                           <ContactLink
                              href={seller.facebookUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                           >
                              💬 Message on Facebook
                           </ContactLink>
                        )}
                     </Contacts>
                  </>
               )}
            </ProfileCol>

            {/* ── Right: the checkout panel ────────────────────────── */}
            <BuyCol>
               <BuyCard>
                  <BuyTitle>Buy license credits</BuyTitle>

                  <PriceRow>
                     <PriceMain>₱{price.toLocaleString()}</PriceMain>
                     <PriceUnit>per credit</PriceUnit>
                  </PriceRow>
                  {seller.kind === 'distributor' &&
                     seller.resellerPricePerCredit != null && (
                        <PriceNote>
                           ₱{seller.resellerPricePerCredit.toLocaleString()} per
                           credit for this distributor&apos;s approved resellers.
                        </PriceNote>
                     )}

                  <StockRow $out={soldOut}>
                     {soldOut
                        ? 'No credits available right now'
                        : `${stock.toLocaleString()} credit${
                             stock === 1 ? '' : 's'
                          } in stock`}
                  </StockRow>

                  {/* On a phone this whole group leaves the card and pins to the
                      bottom of the screen — Buy Now was below the fold, which is
                      a bad place for the only button that earns the seller money. */}
                  <ActionBar>
                     <QtyBlock>
                        <QtyLabel>How many credits?</QtyLabel>
                        <Stepper>
                           <StepBtn
                              type="button"
                              aria-label="Fewer credits"
                              disabled={soldOut || clampedQty <= 1}
                              onClick={() => setQty(clampedQty - 1)}
                           >
                              −
                           </StepBtn>
                           <QtyInput
                              type="number"
                              min={1}
                              max={maxQty}
                              value={clampedQty}
                              disabled={soldOut}
                              onChange={(e) => {
                                 const n = Number(e.target.value);
                                 setQty(Number.isFinite(n) && n > 0 ? n : 1);
                              }}
                           />
                           <StepBtn
                              type="button"
                              aria-label="More credits"
                              disabled={soldOut || clampedQty >= maxQty}
                              onClick={() => setQty(clampedQty + 1)}
                           >
                              +
                           </StepBtn>
                        </Stepper>
                     </QtyBlock>

                     <TotalRow>
                        <TotalLabel>Total</TotalLabel>
                        <TotalVal>₱{total.toLocaleString()}</TotalVal>
                     </TotalRow>

                     <BuyBtn
                        $kind={seller.kind}
                        disabled={soldOut}
                        onClick={handleBuy}
                     >
                        {soldOut ? 'Out of stock' : 'Buy now →'}
                     </BuyBtn>

                     {/* Signed-in buyers already know what happens next, so the
                         pinned bar drops this line and stays short for them. */}
                     <FinePrint $mobileHidden={isAuthenticated}>
                        {isAuthenticated
                           ? `You send the request, ${seller.displayName} confirms it and tells you where to pay.`
                           : `Sign in to continue — we'll bring you straight back to ${seller.displayName}.`}
                     </FinePrint>
                  </ActionBar>
               </BuyCard>

               <PoweredBy>
                  Powered by <strong>SmartFi</strong>
               </PoweredBy>
            </BuyCol>
         </Columns>
      </Shell>
   );
};

export default SellerPage;

/* ─── styles ──────────────────────────────────────────────────────────
   Card language matches the Partners marketplace (surface cards, stat
   pills, gradient CTA) so arriving from a share link doesn't feel like a
   different product. Distributors keep the gold accent, resellers blue —
   the same split used across the partner listings.                     */

const accentOf = (kind: 'distributor' | 'reseller') =>
   kind === 'distributor' ? '#c9a227' : colors.PRIMARY;

const Shell = styled.div`
   min-height: 100vh;
   background: var(--app-bg, ${colors.LIGHT});
   display: flex;
   flex-direction: column;
`;

const TopBar = styled.header`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding: 14px 24px;
   border-bottom: 1px solid ${colors.GRAY13};
   background: var(--app-surface, ${colors.WHITE});
   @media (max-width: 768px) {
      padding: 12px 14px;
   }
`;

const Brand = styled.p`
   margin: 0;
   font-size: 18px;
   font-weight: 900;
   letter-spacing: -0.02em;
   color: ${colors.PRIMARY};
`;

const GhostBtn = styled.button`
   padding: 8px 14px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: ${colors.GRAY_DARK};
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:hover {
      border-color: ${colors.PRIMARY}66;
      color: ${colors.PRIMARY};
   }
`;

const Center = styled.div`
   flex: 1;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 40px 20px;
`;

const Muted = styled.p`
   font-size: 13px;
   color: ${colors.GRAY5};
`;

const NotFoundCard = styled.div`
   max-width: 420px;
   text-align: center;
   padding: 32px 24px;
   border-radius: 16px;
   background: var(--app-surface, ${colors.WHITE});
   border: 1px solid ${colors.GRAY13};
`;

const NotFoundTitle = styled.h1`
   margin: 0 0 8px;
   font-size: 18px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
`;

const NotFoundText = styled.p`
   margin: 0 0 18px;
   font-size: 13px;
   line-height: 1.5;
   color: ${colors.GRAY3};
`;

const Columns = styled.main`
   flex: 1;
   width: 100%;
   max-width: 1080px;
   margin: 0 auto;
   padding: 28px 24px 48px;
   display: grid;
   grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
   gap: 24px;
   align-items: start;
   @media (max-width: 900px) {
      grid-template-columns: minmax(0, 1fr);
      /* Deep bottom padding so the pinned action bar never covers the last
         thing on the page (contact links, Powered by). */
      padding: 16px 12px 180px;
      gap: 14px;
   }
`;

const ProfileCol = styled.section`
   padding: 22px;
   border-radius: 16px;
   background: var(--app-surface, ${colors.WHITE});
   border: 1px solid ${colors.GRAY13};
   @media (max-width: 900px) {
      padding: 14px;
   }
`;

const ProfileHead = styled.div`
   display: flex;
   align-items: center;
   gap: 16px;
   min-width: 0;
`;

const AvatarWrap = styled.div<{ $kind: 'distributor' | 'reseller' }>`
   position: relative;
   width: 76px;
   height: 76px;
   flex-shrink: 0;
   border-radius: 18px;
   overflow: hidden;
   box-shadow: 0 0 0 2px ${({ $kind }) => accentOf($kind)}55;
   @media (max-width: 900px) {
      width: 58px;
      height: 58px;
      border-radius: 14px;
   }
`;

const Avatar = styled.img`
   position: absolute;
   inset: 0;
   width: 100%;
   height: 100%;
   object-fit: cover;
   z-index: 1;
`;

const AvatarFallback = styled.div<{ $kind: 'distributor' | 'reseller' }>`
   position: absolute;
   inset: 0;
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 26px;
   font-weight: 900;
   color: ${colors.WHITE};
   @media (max-width: 900px) {
      font-size: 20px;
   }
   background: ${({ $kind }) =>
      $kind === 'distributor'
         ? 'linear-gradient(135deg, #c9a227, #f0c94b)'
         : `linear-gradient(135deg, ${colors.PRIMARY}, #6a4dff)`};
`;

const HeadText = styled.div`
   min-width: 0;
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const RoleTag = styled.span<{ $kind: 'distributor' | 'reseller' }>`
   align-self: flex-start;
   font-size: 9px;
   font-weight: 800;
   letter-spacing: 0.08em;
   text-transform: uppercase;
   padding: 3px 8px;
   border-radius: 999px;
   color: ${({ $kind }) => accentOf($kind)};
   background: ${({ $kind }) => accentOf($kind)}18;
   border: 1px solid ${({ $kind }) => accentOf($kind)}44;
`;

const SellerName = styled.h1`
   margin: 0;
   font-size: 24px;
   font-weight: 900;
   line-height: 1.15;
   color: ${colors.GRAY_DARK};
   word-break: break-word;
   @media (max-width: 900px) {
      font-size: 20px;
   }
`;

const Location = styled.p`
   margin: 0;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY5};
`;

const Blurb = styled.p`
   margin: 16px 0 0;
   font-size: 13px;
   line-height: 1.6;
   color: ${colors.GRAY3};
   white-space: pre-line;
`;

const SectionTitle = styled.h2`
   margin: 22px 0 10px;
   @media (max-width: 900px) {
      margin: 16px 0 8px;
   }
   font-size: 10px;
   font-weight: 800;
   letter-spacing: 0.08em;
   text-transform: uppercase;
   color: ${colors.GRAY5};
`;

const Perks = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const Perk = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 10px;
   padding: 10px 12px;
   border-radius: 10px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY13};
   @media (max-width: 900px) {
      padding: 8px 10px;
      gap: 8px;
   }
`;

const PerkIcon = styled.span`
   font-size: 16px;
   line-height: 1.2;
`;

const PerkName = styled.p`
   margin: 0;
   font-size: 12.5px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
`;

const PerkSub = styled.p`
   margin: 2px 0 0;
   font-size: 11.5px;
   line-height: 1.45;
   color: ${colors.GRAY5};
`;

const Contacts = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 8px;
`;

const ContactLink = styled.a`
   padding: 8px 12px;
   border-radius: 8px;
   font-size: 12px;
   font-weight: 700;
   text-decoration: none;
   color: ${colors.PRIMARY};
   background: ${colors.PRIMARY}10;
   border: 1px solid ${colors.PRIMARY}33;
   &:hover {
      background: ${colors.PRIMARY}1c;
   }
`;

const BuyCol = styled.aside`
   position: sticky;
   top: 20px;
   display: flex;
   flex-direction: column;
   gap: 10px;
   @media (max-width: 900px) {
      position: static;
   }
`;

const BuyCard = styled.div`
   padding: 20px;
   border-radius: 16px;
   background: var(--app-surface, ${colors.WHITE});
   border: 1px solid ${colors.GRAY13};
   box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
   @media (max-width: 900px) {
      padding: 16px;
   }
`;

/* Desktop: an ordinary block at the foot of the checkout card.
   Mobile: lifted out and pinned to the bottom of the viewport, because the
   button that earns the seller money must never need a scroll to reach.
   z-index stays well under the modal layer (1500) so the buy form covers it. */
const ActionBar = styled.div`
   @media (max-width: 900px) {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 30;
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      grid-template-areas:
         'qty total'
         'buy buy'
         'fine fine';
      align-items: center;
      gap: 8px 12px;
      padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px));
      background: var(--app-surface, ${colors.WHITE});
      border-top: 1px solid ${colors.GRAY13};
      box-shadow: 0 -6px 20px rgba(0, 0, 0, 0.12);
   }
`;

const BuyTitle = styled.h2`
   margin: 0 0 14px;
   font-size: 15px;
   font-weight: 900;
   color: ${colors.GRAY_DARK};
`;

const PriceRow = styled.div`
   display: flex;
   align-items: baseline;
   gap: 6px;
`;

const PriceMain = styled.span`
   font-size: 30px;
   font-weight: 900;
   letter-spacing: -0.02em;
   color: ${colors.GRAY_DARK};
   @media (max-width: 900px) {
      font-size: 26px;
   }
`;

const PriceUnit = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY5};
`;

const PriceNote = styled.p`
   margin: 6px 0 0;
   font-size: 11px;
   line-height: 1.45;
   color: ${colors.GRAY5};
`;

const StockRow = styled.p<{ $out?: boolean }>`
   margin: 12px 0 0;
   padding: 7px 10px;
   border-radius: 8px;
   font-size: 11.5px;
   font-weight: 800;
   text-align: center;
   color: ${({ $out }) => ($out ? colors.GRAY5 : colors.SUCCESS)};
   background: ${({ $out }) => ($out ? colors.GRAY14 : `${colors.SUCCESS}12`)};
   border: 1px solid
      ${({ $out }) => ($out ? colors.GRAY12 : `${colors.SUCCESS}3d`)};
`;

const QtyBlock = styled.div`
   margin-top: 16px;
   @media (max-width: 900px) {
      grid-area: qty;
      margin-top: 0;
   }
`;

const QtyLabel = styled.p`
   margin: 0 0 8px;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.05em;
   text-transform: uppercase;
   color: ${colors.GRAY5};
   /* The stepper explains itself next to a total; the label only costs height. */
   @media (max-width: 900px) {
      display: none;
   }
`;

const Stepper = styled.div`
   display: grid;
   grid-template-columns: 42px minmax(0, 1fr) 42px;
   gap: 8px;
   @media (max-width: 900px) {
      grid-template-columns: 38px 48px 38px;
      gap: 6px;
   }
`;

const StepBtn = styled.button`
   height: 42px;
   border-radius: 10px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.GRAY14};
   color: ${colors.GRAY_DARK};
   font-size: 18px;
   font-weight: 800;
   cursor: pointer;
   &:hover:not(:disabled) {
      border-color: ${colors.PRIMARY}66;
      color: ${colors.PRIMARY};
   }
   &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
   }
`;

const QtyInput = styled.input`
   height: 42px;
   width: 100%;
   text-align: center;
   border-radius: 10px;
   border: 1px solid ${colors.GRAY12};
   background: var(--app-surface, ${colors.WHITE});
   color: ${colors.GRAY_DARK};
   font-size: 16px;
   font-weight: 800;
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
   /* The native spinners fight the −/+ buttons on desktop. */
   -moz-appearance: textfield;
   &::-webkit-outer-spin-button,
   &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
   }
`;

const TotalRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   margin-top: 16px;
   padding-top: 14px;
   border-top: 1px dashed ${colors.GRAY12};
   @media (max-width: 900px) {
      grid-area: total;
      margin-top: 0;
      padding-top: 0;
      border-top: none;
      justify-content: flex-end;
      gap: 8px;
   }
`;

const TotalLabel = styled.span`
   font-size: 12px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.05em;
   color: ${colors.GRAY5};
`;

const TotalVal = styled.span`
   font-size: 22px;
   font-weight: 900;
   color: ${colors.GRAY_DARK};
   @media (max-width: 900px) {
      font-size: 20px;
   }
`;

const BuyBtn = styled.button<{ $kind: 'distributor' | 'reseller' }>`
   width: 100%;
   margin-top: 16px;
   padding: 13px 16px;
   border-radius: 10px;
   border: none;
   font-size: 13px;
   font-weight: 900;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   color: ${colors.WHITE};
   cursor: pointer;
   background: ${({ $kind }) =>
      $kind === 'distributor'
         ? 'linear-gradient(135deg, #b8891b 0%, #d9ad33 55%, #f0c94b 100%)'
         : `linear-gradient(135deg, ${colors.PRIMARY} 0%, #6a4dff 55%, #8b5cf6 100%)`};
   box-shadow: 0 6px 18px rgba(90, 77, 255, 0.24);
   transition: opacity 0.15s ease;
   &:hover:not(:disabled) {
      opacity: 0.92;
   }
   &:disabled {
      background: ${colors.GRAY12};
      color: ${colors.GRAY5};
      box-shadow: none;
      cursor: not-allowed;
   }
   @media (max-width: 900px) {
      grid-area: buy;
      margin-top: 0;
      padding: 14px 16px;
   }
`;

const FinePrint = styled.p<{ $mobileHidden?: boolean }>`
   margin: 12px 0 0;
   font-size: 11px;
   line-height: 1.5;
   text-align: center;
   color: ${colors.GRAY5};
   @media (max-width: 900px) {
      grid-area: fine;
      margin: 0;
      font-size: 10.5px;
      line-height: 1.4;
      display: ${({ $mobileHidden }) => ($mobileHidden ? 'none' : 'block')};
   }
`;

const PoweredBy = styled.p`
   margin: 0;
   text-align: center;
   font-size: 11px;
   color: ${colors.GRAY5};
   strong {
      color: ${colors.GRAY3};
   }
`;
